import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { orderHistoryService } from '../services/orderHistory';
import { getProxiedImageUrl } from '../utils/imageUtils';
import OrderHistory from './OrderHistory';

// All customer-facing data access goes through the customer-api Edge Function
// (uses service role server-side — bypasses RLS safely, no anon key DB access needed)
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
async function customerApi(action, params = {}) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/customer-api`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ action, ...params }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Request failed');
  return json.data;
}

const FoodTypeIndicator = ({ foodType }) => {
  const isVeg = foodType === 'veg';
  
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '16px',
      height: '16px',
      border: `2px solid ${isVeg ? '#22c55e' : '#ef4444'}`,
      borderRadius: '2px',
      marginRight: '8px',
      backgroundColor: 'white',
      flexShrink: 0
    }}>
      <div style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: isVeg ? '#22c55e' : '#ef4444'
      }} />
    </div>
  );
};

const PublicMenu = () => {
  const { qrCode } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState([]);
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Cart functionality states
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [showOrderHistory, setShowOrderHistory] = useState(false);

  useEffect(() => {
    if (qrCode) {
      loadRestaurantData();
    }
  }, [qrCode]);

  // Block PWA install prompt for customers
  useEffect(() => {
    const preventInstall = (e) => {
      e.preventDefault();
      console.log('Install prompt blocked for customer');
    };
    
    window.addEventListener('beforeinstallprompt', preventInstall);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', preventInstall);
    };
  }, []);

  const loadRestaurantData = async () => {
    try {
      // Find table by QR code (via Edge Function — no direct DB access)
      const tableData = await customerApi('getTable', { qrCode });

      setTable(tableData);
      setRestaurant(tableData.restaurants);

      // Load menu for this restaurant (via Edge Function)
      const menuData = await customerApi('getMenu', { restaurantId: tableData.restaurants.id });

      // Filter only available items
      const availableMenu = menuData.map(category => ({
        ...category,
        menu_items: category.menu_items?.filter(item => item.is_available !== false) || []
      })).filter(category => category.menu_items.length > 0);

      setMenu(availableMenu);
      if (availableMenu.length > 0) {
        setSelectedCategory(availableMenu[0].id);
      }

    } catch (error) {
      console.error('Error loading restaurant data:', error);
      setError('Restaurant or menu not found');
    } finally {
      setLoading(false);
    }
  };

  // Cart functionality
  const getItemQuantityInCart = (itemId) => {
    const cartItem = cart.find(item => item.id === itemId);
    return cartItem ? cartItem.quantity : 0;
  };

  const addToCart = (item) => {
    setCart(prev => {
      const existingItem = prev.find(cartItem => cartItem.id === item.id);
      if (existingItem) {
        return prev.map(cartItem =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(item => item.id !== itemId));
  };

  const updateCartQuantity = (itemId, change) => {
    setCart(prev => {
      return prev.map(item => {
        if (item.id === itemId) {
          const newQuantity = item.quantity + change;
          return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
        }
        return item;
      }).filter(Boolean);
    });
  };

  const placeOrder = async () => {
    if (cart.length === 0) {
      alert('Your cart is empty. Please add items before placing an order.');
      return;
    }
    
    setPlacing(true);
    try {
      // Place order via Edge Function (service role — works with strict RLS)
      const order = await customerApi('placeOrder', {
        restaurantId: restaurant.id,
        tableId: table.id,
        items: cart.map(item => ({
          menu_item_id: item.id,
          quantity: item.quantity,
          unit_price: item.price,
        })),
      });

      // Save to order history
      const orderWithItems = {
        ...order,
        items: cart.map(item => ({
          quantity: item.quantity,
          name: item.name,
          unit_price: item.price,
          total_price: item.price * item.quantity
        }))
      };
      orderHistoryService.saveOrder(orderWithItems, restaurant);

      setCart([]);
      setShowCart(false);
      alert(`Order placed successfully!\nOrder Number: ${order.order_number}\nTotal: ₹${order.total_amount.toFixed(2)}\n\nYour order has been sent to the kitchen.`);
      
    } catch (error) {
      console.error('Error placing order:', error);
      alert('Failed to place order. Please try again or contact restaurant staff.');
    } finally {
      setPlacing(false);
    }
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #FF4444',
          borderTop: '4px solid transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '40px',
          textAlign: 'center',
          boxShadow: '0 15px 40px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>Menu Not Found</h2>
          <p style={{ color: '#666' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      padding: '20px',
      paddingBottom: cartItemCount > 0 ? '120px' : '20px'
    }}>
      <div style={{ 
        maxWidth: '800px', 
        margin: '0 auto',
        padding: '0 10px'
      }}>
        {/* Header with Cart Button - Mobile Responsive */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '20px 20px 0 0',
          padding: '20px',
          background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
          color: 'white',
          position: 'relative'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '20px'
          }}>
            {/* Restaurant Info - Full Width */}
            <div style={{ 
              textAlign: 'left',
              width: '100%'
            }}>
              <h1 style={{ 
                margin: '0 0 8px 0', 
                fontSize: 'clamp(1.8rem, 6vw, 3rem)', 
                fontWeight: '700',
                wordBreak: 'break-word',
                lineHeight: '1.2'
              }}>
                {restaurant?.name}
              </h1>
              <p style={{ 
                margin: '0', 
                fontSize: 'clamp(1rem, 4vw, 1.3rem)', 
                opacity: '0.9' 
              }}>
                {table?.custom_name || `Table ${table?.table_number}`}
              </p>
            </div>
            
            {/* Buttons Container */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              width: '100%'
            }}>
              {/* Cart Button */}
              <button
                onClick={() => setShowCart(!showCart)}
                style={{
                  background: '#92400e',
                  color: '#f7fafc',
                  border: '2px solid white',
                  borderRadius: '50px',
                  padding: '12px 18px',
                  fontSize: '0.95rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(44, 62, 80, 0.4)',
                  flexShrink: 0,
                  minWidth: 'fit-content',
                  height: '48px'
                }}
              >
                <span style={{ fontSize: '1rem', opacity: '0.95', filter: 'brightness(1.3)' }}>🛒</span>
                <span>Cart</span>
                {cartItemCount > 0 && (
                  <span style={{
                    background: 'white',
                    color: '#FF4444',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    marginLeft: '2px'
                  }}>
                    {cartItemCount}
                  </span>
                )}
              </button>
              
              {/* Order History Button */}
              <button
                onClick={() => setShowOrderHistory(true)}
                style={{
                  background: 'linear-gradient(135deg, #28a745, #20c997)',
                  color: 'white',
                  border: '2px solid white',
                  borderRadius: '50px',
                  padding: '12px 18px',
                  fontSize: '0.95rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)',
                  flexShrink: 0,
                  minWidth: 'fit-content',
                  height: '48px'
                }}
              >
                <span>📋</span>
                <span>History ({orderHistoryService.getHistoryCount()})</span>
              </button>
            </div>
          </div>
        </div>

        {/* Category Navigation - Mobile Scrollable */}
        {menu.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            padding: '15px 20px',
            borderBottom: '1px solid #e5e7eb',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <div style={{
              display: 'flex',
              gap: '10px',
              minWidth: 'max-content',
              paddingBottom: '5px'
            }}>
              {menu.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  style={{
                    background: selectedCategory === category.id 
                      ? 'linear-gradient(135deg, #FF4444, #FF6B00)'
                      : 'transparent',
                    color: selectedCategory === category.id ? 'white' : '#666',
                    border: selectedCategory === category.id ? 'none' : '2px solid #e5e7eb',
                    padding: '10px 16px',
                    borderRadius: '25px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  {category.name}
                </button>
              ))}
            </div>
            <style>{`
              div::-webkit-scrollbar {
                display: none;
              }
            `}</style>
          </div>
        )}

        
        {/* Menu Items with Add to Cart */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0 0 20px 20px',
          padding: 'clamp(15px, 4vw, 30px)',
          boxShadow: '0 15px 40px rgba(0,0,0,0.1)'
        }}>
          {menu.filter(category => category.id === selectedCategory)
            .map(category => (
              <div key={category.id}>
                <h2 style={{
                  margin: '0 0 30px 0',
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#333',
                  textAlign: 'center',
                  paddingBottom: '15px',
                  borderBottom: '3px solid #FF6B00'
                }}>
                  {category.name}
                </h2>

                
      <div style={{ display: 'grid', gap: '20px' }}>
        {category.menu_items.map(item => (
          <div key={item.id} style={{
            display: 'flex',
            flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
            gap: '15px',
            padding: '20px',
            backgroundColor: '#fefefe',
            borderRadius: '15px',
            border: '2px solid #f1f3f4',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            transition: 'all 0.3s ease'
          }}>
            {/* Image */}
            {item.image_url && (
              <img
                src={getProxiedImageUrl(item.image_url)}
                alt={item.name}
                style={{
                  width: window.innerWidth <= 480 ? '100%' : '100px',
                  height: window.innerWidth <= 480 ? '200px' : '100px',
                  maxHeight: '200px',
                  objectFit: 'cover',
                  borderRadius: '12px',
                  border: '3px solid #f1f3f4',
                  flexShrink: 0
                }}
              />
            )}
            
            {/* Content and Button Container */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: window.innerWidth <= 480 ? 'stretch' : 'center',
              flex: 1,
              flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
              gap: '15px'
            }}>

              {/* Item Details */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <FoodTypeIndicator foodType={item.food_type || 'veg'} />
                  <h3 style={{
                    margin: '0',
                    fontSize: 'clamp(1.1rem, 4vw, 1.4rem)',
                    fontWeight: '700',
                    color: '#333'
                  }}>
                    {item.name}
                  </h3>
                </div>
                
                {item.description && (
                  <p style={{
                    margin: '0 0 12px 0',
                    color: '#666',
                    fontSize: 'clamp(0.9rem, 3vw, 1rem)',
                    lineHeight: '1.5'
                  }}>
                    {item.description}
                  </p>
                )}

                <div style={{
                  fontSize: 'clamp(1.4rem, 5vw, 1.8rem)',
                  fontWeight: '700',
                  color: '#FF6B00'
                }}>
                  {restaurant?.default_currency || item.currency || '₹'}{item.price}
                </div>
              </div>

              
              {/* Add to Cart Controls */}
              <div style={{ 
                alignSelf: window.innerWidth <= 480 ? 'center' : 'center',
                flexShrink: 0,
                marginTop: window.innerWidth <= 480 ? '15px' : '0'
              }}>
                {getItemQuantityInCart(item.id) === 0 ? (
                  <button
                    onClick={() => addToCart(item)}
                    style={{
                      background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '25px',
                      padding: '12px 24px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 4px 15px rgba(255, 68, 68, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Add to Cart
                  </button>
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    background: 'white',
                    borderRadius: '25px',
                    padding: '6px',
                    border: '2px solid #FF4444',
                    boxShadow: '0 4px 15px rgba(255, 68, 68, 0.2)'
                  }}>
                    <button
                      onClick={() => updateCartQuantity(item.id, -1)}
                      style={{
                        background: '#FF4444',
                        border: 'none',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.2rem',
                        fontWeight: 'bold'
                      }}
                    >
                      -
                    </button>
                    
                    <span style={{
                      fontSize: '1.2rem',
                      fontWeight: '700',
                      color: '#FF4444',
                      minWidth: '30px',
                      textAlign: 'center'
                    }}>
                      {getItemQuantityInCart(item.id)}
                    </span>
                    
                    <button
                      onClick={() => addToCart(item)}
                      style={{
                        background: '#FF6B00',
                        border: 'none',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.2rem',
                        fontWeight: 'bold'
                      }}
                    >
                      +
                    </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
  </div>

      {/* Floating Cart Summary */}
      {cartItemCount > 0 && !showCart && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 999
        }}>
          <button
            onClick={() => setShowCart(true)}
            style={{
              background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
              color: 'white',
              border: 'none',
              padding: '15px 30px',
              borderRadius: '50px',
              fontSize: '1.1rem',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 8px 25px rgba(255, 68, 68, 0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div>View Cart ({cartItemCount}) • {restaurant?.default_currency || '₹'}{cartTotal.toFixed(2)}</div>
              <div style={{ fontSize: '0.7rem', opacity: '0.9' }}>*Excl. taxes</div>
          </div>
          </button>
        </div>
      )}

      {/* Cart Slide-out */}
      {showCart && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'rgba(0,0,0,0.5)',
          zIndex: '1000',
          backdropFilter: 'blur(5px)'
        }} onClick={() => setShowCart(false)}>
          <div style={{
            position: 'absolute',
            right: '0',
            top: '0',
            bottom: '0',
            width: '400px',
            maxWidth: '100vw',
            background: 'white',
            boxShadow: '-10px 0 40px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column'
          }} onClick={(e) => e.stopPropagation()}>
            
            {/* Cart Header */}
            <div style={{
              padding: '25px',
              borderBottom: '1px solid #eee',
              background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
              color: 'white'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: '0', fontSize: '1.5rem', fontWeight: '700' }}>
                  Your Order ({cartItemCount})
                </h2>
                <button
                  onClick={() => setShowCart(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '1.2rem'
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Cart Items */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
              {cart.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🛒</div>
                  <p style={{ margin: '0', fontSize: '1.1rem' }}>Your cart is empty</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '15px' }}>
                  {cart.map(item => (
                    <div key={item.id} style={{
                      background: '#f8f9fa',
                      borderRadius: '12px',
                      padding: '15px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ 
                            margin: '0 0 5px 0', 
                            fontSize: '1.1rem', 
                            fontWeight: '600',
                            color: '#333'
                          }}>
                            {item.name}
                          </h4>
                          <p style={{ 
                            margin: '0', 
                            color: '#FF6B00', 
                            fontSize: '0.9rem',
                            fontWeight: '600'
                          }}>
                            {restaurant?.default_currency || item.currency || '₹'}{item.price} each
                          </p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          style={{
                            background: '#dc3545',
                            border: 'none',
                            borderRadius: '6px',
                            color: 'white',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <button
                            onClick={() => updateCartQuantity(item.id, -1)}
                            style={{
                              background: '#FF4444',
                              border: 'none',
                              borderRadius: '6px',
                              color: 'white',
                              padding: '6px 10px',
                              cursor: 'pointer',
                              fontWeight: 'bold'
                            }}
                          >
                            −
                          </button>
                          <span style={{ 
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            minWidth: '30px',
                            textAlign: 'center'
                          }}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateCartQuantity(item.id, 1)}
                            style={{
                              background: '#FF6B00',
                              border: 'none',
                              borderRadius: '6px',
                              color: 'white',
                              padding: '6px 10px',
                              cursor: 'pointer',
                              fontWeight: 'bold'
                            }}
                          >
                            +
                          </button>
                        </div>
                        
                        <div style={{
                          fontSize: '1.1rem',
                          fontWeight: '700',
                          color: '#333'
                        }}>
                          {restaurant?.default_currency || '₹'}{(item.price * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cart Footer */}
            {cart.length > 0 && (
              <div style={{
                padding: '25px',
                borderTop: '1px solid #eee',
                background: '#f8f9fa'
              }}>
                <div style={{ marginBottom: '20px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontSize: '1.3rem', fontWeight: '700', color: '#333' }}>
                    Total: {restaurant?.default_currency || '₹'}{cartTotal.toFixed(2)}
                  </span>
                </div>
                <p style={{
                  margin: '0',
                  fontSize: '0.85rem',
                  color: '#666',
                  fontStyle: 'italic'
                }}>
                  *This amount is excluding of taxes
                </p>
              </div>
                
                <button
                  onClick={placeOrder}
                  disabled={placing}
                  style={{
                    width: '100%',
                    background: placing ? '#ccc' : 'linear-gradient(135deg, #FF4444, #FF6B00)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '15px',
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    cursor: placing ? 'not-allowed' : 'pointer',
                    boxShadow: placing ? 'none' : '0 6px 20px rgba(255, 68, 68, 0.4)'
                  }}
                >
                  {placing ? 'Placing Order...' : 'Place Order'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Order History Modal */}
      {showOrderHistory && (
        <OrderHistory onClose={() => setShowOrderHistory(false)} />
      )}
    </div>
  );
};

export default PublicMenu;